import { useFormikContext } from 'formik';
import { camelCase } from 'lodash';
import moment from 'moment';
import { useEffect, useMemo } from 'react';

import { type InputComponent } from '..';
import { InputDate } from '../input-date';
import { Stack } from '@mui/material';

export interface InputDateRangeProps extends InputComponent {
  min?: moment.Moment;
  max?: moment.Moment;
  step?: number;
}

const DATE_FORMAT = 'YYYY-MM-DD';

export function InputDateRange({
  label,
  nameOverride,
  min,
  max,
  showLabel = false,
  step = 7,
  size = 'sm',
}: InputDateRangeProps) {
  const { setFieldValue, values } =
    useFormikContext<Record<string, { start: string; end: string }>>();

  const name = useMemo(
    () => nameOverride ?? camelCase(label),
    [label, nameOverride],
  );

  useEffect(() => {
    if (values[name] && values[name].start) {
      const momentStart = moment(values[name].start);
      const momentEnd = moment(values[name].end);
      if (momentStart.isAfter(momentEnd)) {
        setFieldValue(
          `${name}.end`,
          momentStart.add(step, 'days').format(DATE_FORMAT),
        );
      }
    }
  }, [name, setFieldValue, step, values]);

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <InputDate
        label="Start Date"
        nameOverride={`${name}.start`}
        showLabel={showLabel}
        min={min?.format(DATE_FORMAT)}
        max={
          values[name].end
            ? moment(values[name].end).format(DATE_FORMAT)
            : max?.format(DATE_FORMAT)
        }
        required
        size={size}
      />
      <div>{'-'}</div>
      <InputDate
        label="End Date"
        nameOverride={`${name}.end`}
        showLabel={showLabel}
        min={
          values[name].start
            ? moment(values[name].start).format(DATE_FORMAT)
            : min?.format(DATE_FORMAT)
        }
        max={max?.format(DATE_FORMAT)}
        required
        size={size}
      />
    </Stack>
  );
}
